// Cognito auth backend (production). aws-amplify v6 owns the session (USER_SRP_AUTH, no client
// secret); MFA is enforced via the API's requireMfa. The cc-command ACCESS token is read fresh per
// call (amplify silently refreshes via the refresh-token flow). This bridge adapts amplify to the
// app's AppAuth surface, including the server authorization probe.
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Amplify } from "aws-amplify";
import {
  fetchAuthSession,
  fetchUserAttributes,
  signOut as amplifySignOut,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { COGNITO_CLIENT_ID, COGNITO_USER_POOL_ID } from "../config";
import { AuthContext, type AppAuth } from "./context";
import { probeCommandAuthz } from "./probe";

let configured = false;
function configureAmplify() {
  if (configured) return;
  configured = true;
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: COGNITO_USER_POOL_ID,
        userPoolClientId: COGNITO_CLIENT_ID,
      },
    },
  });
}

export function CognitoAuthProvider({ children }: { children: ReactNode }) {
  configureAmplify();

  const [isLoaded, setIsLoaded] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [email, setEmail] = useState<string | null>(null);

  // Cognito's session only proves identity, not that this account is authorized for the command
  // portal. Once signed in, probe /v1/command/me with the access token; the shell gate holds on
  // "checking" and shows the rejection wall on "denied".
  const [authz, setAuthz] = useState<AppAuth["authz"]>("checking");
  const [role, setRole] = useState<string | null>(null);

  const getCommandToken = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString() ?? null;
    } catch {
      return null;
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      const signedIn = Boolean(session.tokens?.accessToken);
      setIsSignedIn(signedIn);
      if (signedIn) {
        try {
          const attrs = await fetchUserAttributes();
          setEmail(attrs.email ?? null);
        } catch {
          setEmail(null);
        }
      } else {
        setEmail(null);
      }
    } catch {
      setIsSignedIn(false);
      setEmail(null);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      switch (payload.event) {
        case "signedIn":
        case "signedOut":
        case "tokenRefresh":
        case "tokenRefresh_failure":
          void refresh();
          break;
      }
    });
    return unsubscribe;
  }, [refresh]);

  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      // Not signed in → the gate redirects to sign-in before authz matters; reset for next session.
      setAuthz("checking");
      setRole(null);
      return;
    }
    let active = true;
    setAuthz("checking");
    void probeCommandAuthz(getCommandToken).then((result) => {
      if (!active) return;
      setAuthz(result.authz);
      setRole(result.role);
    });
    return () => {
      active = false;
    };
  }, [isLoaded, isSignedIn, getCommandToken]);

  const value = useMemo<AppAuth>(
    () => ({
      isLoaded,
      isSignedIn,
      authz,
      role,
      displayName: email,
      // Any failure (offline, expired refresh token) resolves to "no token" so the API client falls
      // through to unauthenticated rather than crashing.
      getToken: getCommandToken,
      signOut: async () => {
        await amplifySignOut();
      },
    }),
    [isLoaded, isSignedIn, authz, role, email, getCommandToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
